import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { createEntity, reset } from './grupo-empresarial.reducer';

export interface GrupoCrearFormValues {
  alias: string;
  grpAliasEquilibrio: string;
  grpAliasDecomsa: string;
  /** ISO yyyy-MM-dd para el API (fechaAlta) */
  fechaAlta: string;
}

function defaultFechaAlta(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function makeEmptyForm(): GrupoCrearFormValues {
  return {
    alias: '',
    grpAliasEquilibrio: '',
    grpAliasDecomsa: '',
    fechaAlta: defaultFechaAlta(),
  };
}

const fields: FormField<GrupoCrearFormValues>[] = [
  { name: 'alias', label: 'Alias', required: true, placeholder: 'Alias del grupo' },
  { name: 'grpAliasEquilibrio', label: 'Alias equilibrio', placeholder: 'Alias equilibrio' },
  { name: 'grpAliasDecomsa', label: 'Alias decomsa', placeholder: 'Alias decomsa' },
  { name: 'fechaAlta', label: 'Fecha alta', type: 'date', required: true },
];

const ACCENT = '#6366f1';

export interface GrupoCrearModalProps {
  open: boolean;
  /** Mientras el modal global de 403 está visible: no mostrar el portal pero mantener estado (misma ruta). */
  forceHidden?: boolean;
  onClose: () => void;
  /** Tras crear correctamente (p. ej. navegar al grupo). */
  onCreated?: (grupoId: number) => void;
}

export default function GrupoCrearModal({ open, forceHidden = false, onClose, onCreated }: GrupoCrearModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);
  const updateSuccess = useAppSelector(s => s.grupoEmpresarial.updateSuccess);
  const entity = useAppSelector(s => s.grupoEmpresarial.entity) as { id?: number } | null;

  const [form, setForm] = useState<GrupoCrearFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof GrupoCrearFormValues, string>>>({});
  /** Evita confundir updateSuccess/entity viejos (p. ej. tras visitar un grupo) con un alta recién hecha. */
  const createSubmittedRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) {
      createSubmittedRef.current = false;
      return;
    }
    createSubmittedRef.current = false;
    dispatch(reset());
    setForm(makeEmptyForm());
    setErrors({});
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) {
      createSubmittedRef.current = false;
      return;
    }
    if (!createSubmittedRef.current || updating) return;
    if (!updateSuccess || entity?.id == null) {
      createSubmittedRef.current = false;
      return;
    }
    const newId = entity.id;
    createSubmittedRef.current = false;
    dispatch(reset());
    if (onCreated) {
      onCreated(newId);
    } else {
      onClose();
    }
  }, [open, updating, updateSuccess, entity?.id, dispatch, onCreated, onClose]);

  const handleFieldChange = (name: keyof GrupoCrearFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof GrupoCrearFormValues, string>> = {};
    if (!form.alias.trim()) errs.alias = 'El alias es requerido';
    if (!form.fechaAlta.trim()) errs.fechaAlta = 'La fecha alta es requerida';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    createSubmittedRef.current = true;
    dispatch(
      createEntity({
        alias: form.alias.trim(),
        grpAliasEquilibrio: form.grpAliasEquilibrio.trim(),
        grpAliasDecomsa: form.grpAliasDecomsa.trim(),
        fechaAlta: form.fechaAlta.trim(),
      }),
    );
  };

  if (!open) return null;

  if (forceHidden) return null;

  return (
    <ProfaseModal
      open
      titleId="grupo-crear-modal-title"
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <h2 id="grupo-crear-modal-title" className="profase-modal-title">
        Crear grupo empresarial
      </h2>
      <p className="profase-modal-description">
        Complete los datos del nuevo grupo.
      </p>
      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={onClose}
        accentColor={ACCENT}
        submitLabel="Crear grupo"
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}
